import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { 
  CreateExpenseRequest,
  UpdateExpenseRequest,
  DeleteExpenseRequest,
  ExpenseWithCategory,
  RecentExpensesResponse,
  ExpenseFilters,
  ExpenseSort,
  ExpenseStats
} from '../../types/expense';
import { ExpenseAPI, ExpenseHelpers } from '../../lib/ExpenseManagement';

// Async thunks
export const createExpense = createAsyncThunk(
  'expenses/create',
  async (expenseData: CreateExpenseRequest) => {
    return await ExpenseAPI.createExpense(expenseData);
  }
);

export const fetchRecentExpenses = createAsyncThunk(
  'expenses/fetchRecent',
  async (params: {
    couple_id: string;
    page?: number;
    limit?: number;
    filters?: ExpenseFilters;
    sort?: ExpenseSort;
  }) => {
    return await ExpenseAPI.getRecentExpenses(params);
  }
);

export const updateExpense = createAsyncThunk(
  'expenses/update',
  async (expenseData: UpdateExpenseRequest) => {
    return await ExpenseAPI.updateExpense(expenseData);
  }
);

export const deleteExpense = createAsyncThunk(
  'expenses/delete',
  async (deleteData: DeleteExpenseRequest) => {
    await ExpenseAPI.deleteExpense(deleteData);
    return deleteData.id;
  }
);

interface ExpenseState {
  // Expense data
  expenses: ExpenseWithCategory[];
  totalCount: number;
  currentPage: number;
  limit: number;
  
  // Loading states
  isLoading: boolean;
  isCreating: boolean;
  isUpdating: boolean;
  isDeleting: boolean;
  
  // UI state
  filters: ExpenseFilters;
  sort: ExpenseSort;
  
  // Statistics
  stats: ExpenseStats | null;
  
  // Selected expense for editing
  selectedExpense: ExpenseWithCategory | null;
  
  // Error handling
  error: string | null;
  createError: string | null;
  updateError: string | null;
  deleteError: string | null;
}

const initialState: ExpenseState = {
  expenses: [],
  totalCount: 0,
  currentPage: 1,
  limit: 20,
  
  isLoading: false,
  isCreating: false,
  isUpdating: false,
  isDeleting: false,
  
  filters: {},
  sort: {
    field: 'expense_date',
    direction: 'desc',
  },
  
  stats: null,
  selectedExpense: null,
  
  error: null,
  createError: null,
  updateError: null,
  deleteError: null,
};

const expenseSlice = createSlice({
  name: 'expenses',
  initialState,
  reducers: {
    // Clear errors
    clearErrors: (state) => {
      state.error = null;
      state.createError = null;
      state.updateError = null;
      state.deleteError = null;
    },
    
    // Update filters
    setFilters: (state, action: PayloadAction<ExpenseFilters>) => {
      state.filters = action.payload;
      state.currentPage = 1; // Reset to first page when filters change
    },
    
    // Clear filters
    clearFilters: (state) => {
      state.filters = {};
      state.currentPage = 1;
    },
    
    // Update sort
    setSort: (state, action: PayloadAction<ExpenseSort>) => {
      state.sort = action.payload;
      state.currentPage = 1; // Reset to first page when sort changes
    },
    
    // Set current page
    setCurrentPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    
    // Set limit
    setLimit: (state, action: PayloadAction<number>) => {
      state.limit = action.payload;
      state.currentPage = 1; // Reset to first page when limit changes
    },
    
    // Select expense for editing
    selectExpense: (state, action: PayloadAction<ExpenseWithCategory | null>) => {
      state.selectedExpense = action.payload;
    },
    
    // Update stats when expenses change
    updateStats: (state) => {
      state.stats = ExpenseHelpers.calculateStats(state.expenses);
    },
    
    // Reset state
    resetExpenseState: (state) => {
      return { ...initialState };
    },
  },
  extraReducers: (builder) => {
    // Create expense
    builder
      .addCase(createExpense.pending, (state) => {
        state.isCreating = true;
        state.createError = null;
      })
      .addCase(createExpense.fulfilled, (state, action) => {
        state.isCreating = false;
        // Add the new expense to the beginning of the list
        state.expenses.unshift(action.payload);
        state.totalCount += 1;
        // Update stats
        state.stats = ExpenseHelpers.calculateStats(state.expenses);
      })
      .addCase(createExpense.rejected, (state, action) => {
        state.isCreating = false;
        state.createError = action.error.message || 'Failed to create expense';
      });
    
    // Fetch recent expenses
    builder
      .addCase(fetchRecentExpenses.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchRecentExpenses.fulfilled, (state, action) => {
        state.isLoading = false;
        const { expenses, total_count, page, limit } = action.payload;
        
        if (page === 1) {
          // Replace expenses for first page
          state.expenses = expenses;
        } else {
          // Append expenses for pagination
          state.expenses.push(...expenses);
        }
        
        state.totalCount = total_count;
        state.currentPage = page;
        state.limit = limit;
        
        // Update stats
        state.stats = ExpenseHelpers.calculateStats(state.expenses);
      })
      .addCase(fetchRecentExpenses.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to fetch expenses';
      });
    
    // Update expense
    builder
      .addCase(updateExpense.pending, (state) => {
        state.isUpdating = true;
        state.updateError = null;
      })
      .addCase(updateExpense.fulfilled, (state, action) => {
        state.isUpdating = false;
        // Update the expense in the list
        const index = state.expenses.findIndex(e => e.id === action.payload.id);
        if (index !== -1) {
          state.expenses[index] = action.payload;
        }
        // Clear selected expense
        state.selectedExpense = null;
        // Update stats
        state.stats = ExpenseHelpers.calculateStats(state.expenses);
      })
      .addCase(updateExpense.rejected, (state, action) => {
        state.isUpdating = false;
        state.updateError = action.error.message || 'Failed to update expense';
      });
    
    // Delete expense
    builder
      .addCase(deleteExpense.pending, (state) => {
        state.isDeleting = true;
        state.deleteError = null;
      })
      .addCase(deleteExpense.fulfilled, (state, action) => {
        state.isDeleting = false;
        // Remove the expense from the list
        state.expenses = state.expenses.filter(e => e.id !== action.payload);
        state.totalCount -= 1;
        // Clear selected expense if it was deleted
        if (state.selectedExpense?.id === action.payload) {
          state.selectedExpense = null;
        }
        // Update stats
        state.stats = ExpenseHelpers.calculateStats(state.expenses);
      })
      .addCase(deleteExpense.rejected, (state, action) => {
        state.isDeleting = false;
        state.deleteError = action.error.message || 'Failed to delete expense';
      });
  },
});

export const {
  clearErrors,
  setFilters,
  clearFilters,
  setSort,
  setCurrentPage,
  setLimit,
  selectExpense,
  updateStats,
  resetExpenseState,
} = expenseSlice.actions;

export default expenseSlice.reducer;